import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { whatsappService } from "@/services/whatsapp-service";
import {
  isReengagementEligible,
  SendTemplatePayload,
  SendTextPayload,
  WhatsAppConversation,
  WhatsAppMessage,
  WhatsAppTemplate,
} from "@/types/whatsapp";

export const whatsappKeys = {
  all: ["whatsapp"] as const,
  templates: (hotelId: string) =>
    [...whatsappKeys.all, "templates", hotelId] as const,
  conversation: (hotelId: string, conversationId: string) =>
    [...whatsappKeys.all, "conversation", hotelId, conversationId] as const,
  resolveConversation: (
    hotelId: string,
    bookingId: string,
    streamChannelId: string,
  ) =>
    [
      ...whatsappKeys.all,
      "resolveConversation",
      hotelId,
      bookingId,
      streamChannelId,
    ] as const,
  messages: (conversationId: string) =>
    [...whatsappKeys.all, "messages", conversationId] as const,
};

/**
 * Approved templates for the re-engagement picker. Fetched only when the picker
 * opens (`enabled`). Any APPROVED template can be sent into a closed 24h window —
 * a Quick-Reply / re-engagement template just makes it easier for the guest to
 * reopen the chat — so we keep all of them and only sort the re-engagement-eligible
 * ones first (they're also badged in the dialog).
 */
export const useApprovedTemplates = (hotelId: string, enabled: boolean) =>
  useQuery<WhatsAppTemplate[], unknown, WhatsAppTemplate[]>({
    queryKey: whatsappKeys.templates(hotelId),
    queryFn: () => whatsappService.listApprovedTemplates(hotelId),
    enabled: enabled && !!hotelId,
    staleTime: 5 * 60 * 1000,
    select: (templates) =>
      templates
        .filter((template) => template.status === "APPROVED")
        .sort(
          (a, b) =>
            Number(isReengagementEligible(b)) -
            Number(isReengagementEligible(a)),
        ),
  });

export const useConversation = (
  hotelId: string,
  conversationId: string,
  enabled = true,
) =>
  useQuery<WhatsAppConversation>({
    queryKey: whatsappKeys.conversation(hotelId, conversationId),
    queryFn: () => whatsappService.getConversation(hotelId, conversationId),
    enabled: enabled && !!hotelId && !!conversationId,
    staleTime: 5 * 60 * 1000,
  });

/**
 * Resolves — creating if needed — the WhatsApp conversation backing a booking,
 * so a re-engagement template can be sent to a guest who has never messaged.
 * Calls the backend resolve-or-create endpoint (derives the guest phone from
 * the booking, upserts the conversation, stamps the Stream-channel link).
 * Fetched lazily — only when `enabled` (the re-engagement dialog is opening).
 */
export const useResolveConversation = (
  hotelId: string | null | undefined,
  bookingId: string | null | undefined,
  streamChannelId: string | null | undefined,
  enabled: boolean,
) =>
  useQuery<WhatsAppConversation>({
    queryKey: whatsappKeys.resolveConversation(
      hotelId ?? "",
      bookingId ?? "",
      streamChannelId ?? "",
    ),
    queryFn: () =>
      whatsappService.resolveConversation(hotelId as string, {
        bookingId: bookingId as string,
        streamChannelId: streamChannelId ?? undefined,
      }),
    enabled: enabled && !!hotelId && !!bookingId,
    staleTime: 5 * 60 * 1000,
    retry: false, // a 404 (no phone / no booking) should surface immediately
  });

export const useSendText = (hotelId: string, conversationId: string) => {
  const qc = useQueryClient();
  return useMutation<WhatsAppMessage, unknown, SendTextPayload>({
    mutationFn: (body) =>
      whatsappService.sendText(hotelId, conversationId, body),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: whatsappKeys.messages(conversationId),
      });
    },
  });
};

export const useSendTemplate = (hotelId: string, conversationId: string) => {
  const qc = useQueryClient();
  return useMutation<WhatsAppMessage, unknown, SendTemplatePayload>({
    mutationFn: (body) =>
      whatsappService.sendTemplate(hotelId, conversationId, body),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: whatsappKeys.messages(conversationId),
      });
      // Window stays closed until the guest replies, but refetch so the composer
      // flips back to free-text the moment `windowOpenUntil` advances.
      qc.invalidateQueries({
        queryKey: whatsappKeys.conversation(hotelId, conversationId),
      });
    },
  });
};
